/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { ArrowRight, Sparkles, Clock, ShieldCheck } from 'lucide-react';
import { QuickLeadForm } from './QuickLeadForm';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const PROMISES = [
    { icon: Clock, label: '20 minutes', sub: 'Démonstration ciblée' },
    { icon: ShieldCheck, label: 'Sans engagement', sub: 'Aucune obligation' },
    { icon: Sparkles, label: 'Sur vos données', sub: 'Pas un argumentaire générique' },
];

/**
 * ConvFinalCTA — Iter170. Section de clôture orientée 100% conversion.
 *
 * Double opportunité de capture :
 *   - Formulaire visible (QuickLeadForm) pour les visiteurs qui veulent
 *     « être recontactés » sans remplir le grand formulaire de démo.
 *   - Bouton primaire vers le modal complet pour ceux qui sont déjà
 *     prêts à donner toutes les infos.
 */
export const ConvFinalCTA = ({ onDemoClick }) => {
    return (
        <section className="relative py-24 md:py-36 overflow-hidden" data-testid="final-cta-section">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[460px] rounded-full bg-[#681793]/22 blur-[160px]" />
                <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-[#FF914D]/12 blur-[140px]" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7 }}
                className="relative max-w-3xl mx-auto px-6 md:px-12 text-center"
            >
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF914D]/30 bg-[#FF914D]/8 text-[10px] font-medium tracking-widest text-[#FF914D] uppercase"
                      style={MONO}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF914D] animate-pulse" />
                    Prêt à voir Homly sur vos chiffres ?
                </span>

                <h2 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tighter text-white leading-[1.05]" style={DISPLAY}>
                    Échangeons{' '}
                    <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                        20 minutes.
                    </span>
                </h2>
                <p className="mt-5 text-base md:text-lg text-[#A19DAB] max-w-xl mx-auto leading-relaxed">
                    Pas d'argumentaire générique. On regarde ensemble votre organisation
                    et ce que Homly peut concrètement apporter à vos équipes.
                </p>

                {/* Trois promesses courtes */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
                    {PROMISES.map(({ icon: Icon, label, sub }, i) => (
                        <motion.div
                            key={label}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.4, delay: 0.08 * i }}
                            className="rounded-2xl border border-white/8 bg-white/[0.02] p-4"
                        >
                            <Icon className="w-5 h-5 text-[#FF914D] mx-auto mb-2" />
                            <p className="text-sm font-semibold text-white" style={DISPLAY}>{label}</p>
                            <p className="text-[11px] text-[#A19DAB] mt-0.5">{sub}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Formulaire de capture rapide */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.55, delay: 0.25 }}
                    className="mt-10"
                    data-testid="final-cta-quick-lead-wrap"
                >
                    <QuickLeadForm
                        variant="card"
                        label="Laissez-nous vos coordonnées, on revient vers vous dans la journée."
                        ctaLabel="Être recontacté"
                        source="final_cta"
                        data-testid="final-cta-quick-lead"
                    />
                </motion.div>

                {/* OU divider */}
                <div className="mt-8 flex items-center gap-4 max-w-md mx-auto">
                    <div className="flex-1 h-px bg-white/8" />
                    <span className="text-[10px] tracking-widest text-white/40 uppercase" style={MONO}>
                        ou
                    </span>
                    <div className="flex-1 h-px bg-white/8" />
                </div>

                {/* Bouton modal complet */}
                <div className="mt-6 flex justify-center">
                    <Button
                        onClick={onDemoClick}
                        data-testid="footer-demo-request-button"
                        className="group bg-white/8 hover:bg-white/14 border border-white/15 text-white font-semibold text-sm px-7 py-5 rounded-full transition-all"
                    >
                        Remplir le formulaire de démo complet
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                </div>
            </motion.div>
        </section>
    );
};
